/**
 * Unified Conversation Service
 * Handles all conversation persistence with both localStorage and Firestore
 * Provides offline support, retry logic, and data consistency
 */

import {
  createConversation as createFirestoreConversation,
  getConversations as getFirestoreConversations,
  updateConversation as updateFirestoreConversation,
  deleteConversation as deleteFirestoreConversation,
  addMessage as addFirestoreMessage,
  getMessages as getFirestoreMessages,
  getUserProfile,
  createOrUpdateUserProfile
} from '../utils/firestore';

// Constants
const STORAGE_KEYS = {
  CONVERSATIONS: 'agency_superchat_conversations',
  OFFLINE_QUEUE: 'agency_superchat_offline_queue',
  SYNC_STATUS: 'agency_superchat_sync_status',
  LAST_SYNC: 'agency_superchat_last_sync'
};

const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // Start with 1 second
  BACKOFF_MULTIPLIER: 2
};

const SYNC_INTERVAL = 30000; // Sync every 30 seconds

// Utility functions
const log = (level, message, data = {}) => {
  const timestamp = new Date().toISOString();
  const logEntry = { timestamp, level, message, data };

  if (level === 'error') {
    console.error(`[ConversationService] ${message}`, data);
  } else if (level === 'warn') {
    console.warn(`[ConversationService] ${message}`, data);
  } else {
    console.log(`[ConversationService] ${message}`, data);
  }

  // Store critical logs for debugging
  if (level === 'error' || level === 'warn') {
    const logs = JSON.parse(localStorage.getItem('conversation_service_logs') || '[]');
    logs.push(logEntry);
    // Keep only last 100 logs
    if (logs.length > 100) {
      logs.splice(0, logs.length - 100);
    }
    localStorage.setItem('conversation_service_logs', JSON.stringify(logs));
  }
};

const isOnline = () => {
  return navigator.onLine;
};

const generateId = () => {
  return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Storage quota check
const checkStorageQuota = async () => {
  if (navigator.storage && navigator.storage.estimate) {
    const { usage, quota } = await navigator.storage.estimate();
    const percentUsed = (usage / quota) * 100;
    if (percentUsed > 90) {
      log('warn', 'Storage quota warning', { percentUsed, usage, quota });
      return { available: false, percentUsed };
    }
    return { available: true, percentUsed };
  }
  return { available: true, percentUsed: 0 };
};

// Data structure validation
const validateConversation = (conversation) => {
  const required = ['id', 'title', 'messages'];
  for (const field of required) {
    if (!conversation[field]) {
      throw new Error(`Invalid conversation: missing ${field}`);
    }
  }

  if (!Array.isArray(conversation.messages)) {
    throw new Error('Invalid conversation: messages must be an array');
  }

  return true;
};

const validateMessage = (message) => {
  const required = ['content', 'role'];
  for (const field of required) {
    if (!message[field]) {
      throw new Error(`Invalid message: missing ${field}`);
    }
  }

  if (!['user', 'assistant', 'system'].includes(message.role)) {
    throw new Error(`Invalid message: role must be user, assistant, or system`);
  }

  return true;
};

// Offline queue management
class OfflineQueue {
  constructor() {
    this.queue = this.load();
    this.processing = false;
  }

  load() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.OFFLINE_QUEUE);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      log('error', 'Failed to load offline queue', { error: error.message });
      return [];
    }
  }

  save() {
    try {
      localStorage.setItem(STORAGE_KEYS.OFFLINE_QUEUE, JSON.stringify(this.queue));
    } catch (error) {
      log('error', 'Failed to save offline queue', { error: error.message });
    }
  }

  add(operation) {
    this.queue.push({
      ...operation,
      timestamp: Date.now(),
      retries: 0
    });
    this.save();
    log('info', 'Added operation to offline queue', { operation: operation.type });
  }

  async process() {
    if (this.processing || !isOnline() || this.queue.length === 0) {
      return;
    }

    this.processing = true;
    log('info', 'Processing offline queue', { queueSize: this.queue.length });

    const processedIds = [];

    for (const operation of this.queue) {
      try {
        await this.executeOperation(operation);
        processedIds.push(operation.id);
      } catch (error) {
        log('error', 'Failed to process queued operation', {
          operation: operation.type,
          error: error.message
        });

        operation.retries++;
        if (operation.retries >= RETRY_CONFIG.MAX_RETRIES) {
          log('error', 'Max retries reached, removing from queue', { operation });
          processedIds.push(operation.id);
        }
      }
    }

    // Remove processed operations
    this.queue = this.queue.filter(op => !processedIds.includes(op.id));
    this.save();

    this.processing = false;
    log('info', 'Finished processing offline queue', {
      processed: processedIds.length,
      remaining: this.queue.length
    });
  }

  async executeOperation(operation) {
    switch (operation.type) {
      case 'CREATE_CONVERSATION':
        await createFirestoreConversation(operation.userId, operation.data);
        break;
      case 'UPDATE_CONVERSATION':
        await updateFirestoreConversation(operation.userId, operation.conversationId, operation.data);
        break;
      case 'DELETE_CONVERSATION':
        await deleteFirestoreConversation(operation.userId, operation.conversationId);
        break;
      case 'ADD_MESSAGE':
        await addFirestoreMessage(operation.userId, operation.conversationId, operation.data);
        break;
      default:
        throw new Error(`Unknown operation type: ${operation.type}`);
    }
  }
}

// Local storage manager
class LocalStorageManager {
  static getConversations() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CONVERSATIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      log('error', 'Failed to load conversations from localStorage', { error: error.message });
      return [];
    }
  }

  static saveConversations(conversations) {
    try {
      localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, JSON.stringify(conversations));
      return true;
    } catch (error) {
      log('error', 'Failed to save conversations to localStorage', { error: error.message });

      // Try to clear old data if quota exceeded
      if (error.name === 'QuotaExceededError') {
        this.clearOldData();
        try {
          localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, JSON.stringify(conversations));
          return true;
        } catch (retryError) {
          log('error', 'Failed to save even after clearing old data', { error: retryError.message });
          return false;
        }
      }
      return false;
    }
  }

  static clearOldData() {
    log('info', 'Clearing old data to free up storage');

    // Get conversations and sort by last update
    const conversations = this.getConversations();
    const sorted = conversations.sort((a, b) => {
      const aTime = a.updatedAt || a.createdAt || 0;
      const bTime = b.updatedAt || b.createdAt || 0;
      return bTime - aTime;
    });

    // Keep only the most recent 50 conversations
    const toKeep = sorted.slice(0, 50);
    this.saveConversations(toKeep);

    // Clear old logs
    localStorage.removeItem('conversation_service_logs');
  }

  static getConversation(conversationId) {
    const conversations = this.getConversations();
    return conversations.find(c => c.id === conversationId);
  }

  static saveConversation(conversation) {
    const conversations = this.getConversations();
    const index = conversations.findIndex(c => c.id === conversation.id);

    if (index >= 0) {
      conversations[index] = conversation;
    } else {
      conversations.push(conversation);
    }

    return this.saveConversations(conversations);
  }

  static deleteConversation(conversationId) {
    const conversations = this.getConversations();
    const filtered = conversations.filter(c => c.id !== conversationId);
    return this.saveConversations(filtered);
  }

  static addMessageToConversation(conversationId, message) {
    const conversation = this.getConversation(conversationId);
    if (!conversation) {
      log('error', 'Conversation not found in localStorage', { conversationId });
      return false;
    }

    if (!conversation.messages) {
      conversation.messages = [];
    }

    conversation.messages.push(message);
    conversation.updatedAt = Date.now();

    return this.saveConversation(conversation);
  }
}

// Retry mechanism
async function retryOperation(operation, maxRetries = RETRY_CONFIG.MAX_RETRIES) {
  let lastError;
  let delay = RETRY_CONFIG.RETRY_DELAY;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      log('warn', `Retry attempt ${i + 1}/${maxRetries} failed`, { error: error.message });

      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= RETRY_CONFIG.BACKOFF_MULTIPLIER;
      }
    }
  }

  throw lastError;
}

// Main service class
class ConversationService {
  constructor() {
    this.offlineQueue = new OfflineQueue();
    this.syncTimer = null;
    this.userId = null;
    this.isInitialized = false;

    // Set up online/offline listeners
    window.addEventListener('online', () => {
      log('info', 'Network connection restored');
      this.processOfflineQueue();
      this.syncConversations();
    });

    window.addEventListener('offline', () => {
      log('warn', 'Network connection lost');
    });

    // Set up periodic sync
    this.startPeriodicSync();
  }

  async initialize(userId) {
    if (!userId) {
      throw new Error('User ID is required to initialize ConversationService');
    }

    this.userId = userId;
    log('info', 'Initializing ConversationService', { userId });

    try {
      // Ensure user profile exists
      if (isOnline()) {
        let profile = await getUserProfile(userId);
        if (!profile) {
          await createOrUpdateUserProfile(userId, {
            createdAt: Date.now(),
            lastLogin: Date.now()
          });
        }
      }

      this.isInitialized = true;

      // Initial sync
      await this.syncConversations();

      // Process any pending offline operations
      await this.processOfflineQueue();

      log('info', 'ConversationService initialized successfully');
    } catch (error) {
      log('error', 'Failed to initialize ConversationService', { error: error.message });
      // Service can still work in offline mode
      this.isInitialized = true;
    }
  }

  ensureInitialized() {
    if (!this.isInitialized || !this.userId) {
      throw new Error('ConversationService not initialized. Call initialize(userId) first.');
    }
  }

  /**
   * Save a conversation to both localStorage and Firestore
   * @param {Object} conversation - The conversation to save
   * @returns {Promise<Object>} The saved conversation
   */
  async saveConversation(conversation) {
    this.ensureInitialized();

    try {
      // Validate conversation structure
      validateConversation(conversation);

      // Check storage quota
      const { available } = await checkStorageQuota();
      if (!available) {
        log('warn', 'Storage quota exceeded, attempting cleanup');
        LocalStorageManager.clearOldData();
      }

      // Add metadata
      const now = Date.now();
      if (!conversation.id) {
        conversation.id = generateId();
      }
      conversation.updatedAt = now;
      if (!conversation.createdAt) {
        conversation.createdAt = now;
      }

      // Always save to localStorage first (for immediate UI update)
      const savedLocally = LocalStorageManager.saveConversation(conversation);
      if (!savedLocally) {
        log('error', 'Failed to save conversation to localStorage');
      }

      // Try to save to Firestore
      if (isOnline()) {
        try {
          await retryOperation(async () => {
            if (conversation.firestoreId) {
              // Update existing Firestore conversation
              await updateFirestoreConversation(this.userId, conversation.firestoreId, {
                title: conversation.title,
                messages: conversation.messages,
                metadata: conversation.metadata || {},
                characterId: conversation.characterId
              });
            } else {
              // Create new Firestore conversation
              const firestoreId = await createFirestoreConversation(this.userId, {
                title: conversation.title,
                projectId: conversation.projectId,
                messages: conversation.messages || [],
                metadata: conversation.metadata || {},
                characterId: conversation.characterId
              });
              conversation.firestoreId = firestoreId;

              // Update localStorage with Firestore ID
              LocalStorageManager.saveConversation(conversation);
            }
          });

          log('info', 'Conversation saved successfully', { id: conversation.id });
        } catch (error) {
          log('warn', 'Failed to save to Firestore, queuing for later', { error: error.message });
          this.offlineQueue.add({
            id: generateId(),
            type: conversation.firestoreId ? 'UPDATE_CONVERSATION' : 'CREATE_CONVERSATION',
            userId: this.userId,
            conversationId: conversation.firestoreId,
            data: conversation
          });
        }
      } else {
        // Queue for later sync
        log('info', 'Offline: queuing conversation for sync');
        this.offlineQueue.add({
          id: generateId(),
          type: conversation.firestoreId ? 'UPDATE_CONVERSATION' : 'CREATE_CONVERSATION',
          userId: this.userId,
          conversationId: conversation.firestoreId,
          data: conversation
        });
      }

      return conversation;
    } catch (error) {
      log('error', 'Failed to save conversation', { error: error.message });
      throw error;
    }
  }

  /**
   * Load conversations from both localStorage and Firestore
   * @returns {Promise<Array>} Array of conversations
   */
  async loadConversations() {
    // Allow loading from localStorage even if not fully initialized
    // This enables offline-first behavior
    if (!this.isInitialized) {
      log('warn', 'Loading conversations before service is fully initialized');
    }

    try {
      log('info', 'Loading conversations', { userId: this.userId, isInitialized: this.isInitialized });

      // Always start with localStorage data (immediate)
      const localConversations = LocalStorageManager.getConversations();
      log('info', 'Loaded from localStorage', { count: localConversations.length });

      if (!isOnline()) {
        log('info', 'Offline: returning localStorage conversations', { count: localConversations.length });
        return localConversations;
      }

      // Try to fetch from Firestore and merge
      try {
        log('info', 'Attempting to fetch from Firestore', { userId: this.userId });
        const firestoreConversations = await retryOperation(async () => {
          if (!this.userId) {
            log('warn', 'No userId available for Firestore fetch');
            return [];
          }
          return await getFirestoreConversations(this.userId);
        });

        log('info', 'Fetched from Firestore', { count: firestoreConversations.length });

        // Load messages for each conversation from Firestore
        const conversationsWithMessages = await Promise.all(
          firestoreConversations.map(async (conv) => {
            try {
              const messages = await getFirestoreMessages(this.userId, conv.id);
              return {
                ...conv,
                messages: messages || []
              };
            } catch (error) {
              log('warn', 'Failed to load messages for conversation', { conversationId: conv.id, error });
              return {
                ...conv,
                messages: []
              };
            }
          })
        );

        log('info', 'Loaded messages for conversations', {
          conversationsCount: conversationsWithMessages.length,
          totalMessages: conversationsWithMessages.reduce((sum, c) => sum + (c.messages?.length || 0), 0)
        });

        // Merge conversations (Firestore takes precedence for conflicts)
        const merged = this.mergeConversations(localConversations, conversationsWithMessages);

        // Save merged data back to localStorage
        LocalStorageManager.saveConversations(merged);

        log('info', 'Conversations loaded and merged', {
          local: localConversations.length,
          firestore: firestoreConversations.length,
          merged: merged.length
        });

        return merged;
      } catch (error) {
        log('warn', 'Failed to fetch from Firestore, using localStorage', { error: error.message });
        return localConversations;
      }
    } catch (error) {
      log('error', 'Failed to load conversations', { error: error.message });
      throw error;
    }
  }

  /**
   * Add a message to a conversation
   * @param {string} conversationId - The conversation ID
   * @param {Object} message - The message to add
   * @returns {Promise<Object>} The updated conversation
   */
  async addMessage(conversationId, message) {
    this.ensureInitialized();

    try {
      // Validate message
      validateMessage(message);

      // Add metadata
      if (!message.id) {
        message.id = generateMessageId();
      }
      if (!message.timestamp) {
        message.timestamp = Date.now();
      }

      // Get conversation
      const conversation = LocalStorageManager.getConversation(conversationId);
      if (!conversation) {
        throw new Error(`Conversation not found: ${conversationId}`);
      }

      // Add message to localStorage
      LocalStorageManager.addMessageToConversation(conversationId, message);

      // Try to add to Firestore
      if (isOnline() && conversation.firestoreId) {
        try {
          await retryOperation(async () => {
            await addFirestoreMessage(this.userId, conversation.firestoreId, message);
          });
          log('info', 'Message added successfully', { conversationId, messageId: message.id });
        } catch (error) {
          log('warn', 'Failed to add message to Firestore, queuing for later', { error: error.message });
          this.offlineQueue.add({
            id: generateId(),
            type: 'ADD_MESSAGE',
            userId: this.userId,
            conversationId: conversation.firestoreId,
            data: message
          });
        }
      } else {
        // Queue for later sync
        log('info', 'Offline: queuing message for sync');
        this.offlineQueue.add({
          id: generateId(),
          type: 'ADD_MESSAGE',
          userId: this.userId,
          conversationId: conversation.firestoreId,
          data: message
        });
      }

      // Return updated conversation
      return LocalStorageManager.getConversation(conversationId);
    } catch (error) {
      log('error', 'Failed to add message', { error: error.message });
      throw error;
    }
  }

  /**
   * Delete a conversation from both localStorage and Firestore
   * @param {string} conversationId - The conversation ID to delete
   * @returns {Promise<boolean>} Success status
   */
  async deleteConversation(conversationId) {
    this.ensureInitialized();

    try {
      log('info', 'Deleting conversation', { conversationId });

      // Get conversation to find Firestore ID
      const conversation = LocalStorageManager.getConversation(conversationId);

      // Delete from localStorage
      LocalStorageManager.deleteConversation(conversationId);

      // Try to delete from Firestore
      if (isOnline() && conversation && conversation.firestoreId) {
        try {
          await retryOperation(async () => {
            await deleteFirestoreConversation(this.userId, conversation.firestoreId);
          });
          log('info', 'Conversation deleted successfully', { conversationId });
        } catch (error) {
          log('warn', 'Failed to delete from Firestore, queuing for later', { error: error.message });
          this.offlineQueue.add({
            id: generateId(),
            type: 'DELETE_CONVERSATION',
            userId: this.userId,
            conversationId: conversation.firestoreId
          });
        }
      } else if (conversation && conversation.firestoreId) {
        // Queue for later sync
        log('info', 'Offline: queuing deletion for sync');
        this.offlineQueue.add({
          id: generateId(),
          type: 'DELETE_CONVERSATION',
          userId: this.userId,
          conversationId: conversation.firestoreId
        });
      }

      return true;
    } catch (error) {
      log('error', 'Failed to delete conversation', { error: error.message });
      throw error;
    }
  }

  /**
   * Sync conversations between localStorage and Firestore
   * @returns {Promise<Object>} Sync status
   */
  async syncConversations() {
    if (!this.isInitialized || !this.userId) {
      log('warn', 'Cannot sync: service not initialized');
      return { success: false, message: 'Service not initialized' };
    }

    if (!isOnline()) {
      log('info', 'Cannot sync: offline');
      return { success: false, message: 'Offline' };
    }

    try {
      log('info', 'Starting conversation sync');

      const localConversations = LocalStorageManager.getConversations();
      const firestoreConversations = await getFirestoreConversations(this.userId);

      // Merge and resolve conflicts
      const merged = this.mergeConversations(localConversations, firestoreConversations);

      // Save merged data
      LocalStorageManager.saveConversations(merged);

      // Update last sync time
      localStorage.setItem(STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
      localStorage.setItem(STORAGE_KEYS.SYNC_STATUS, JSON.stringify({
        success: true,
        timestamp: Date.now(),
        localCount: localConversations.length,
        firestoreCount: firestoreConversations.length,
        mergedCount: merged.length
      }));

      log('info', 'Conversation sync completed', {
        local: localConversations.length,
        firestore: firestoreConversations.length,
        merged: merged.length
      });

      return {
        success: true,
        localCount: localConversations.length,
        firestoreCount: firestoreConversations.length,
        mergedCount: merged.length
      };
    } catch (error) {
      log('error', 'Sync failed', { error: error.message });

      localStorage.setItem(STORAGE_KEYS.SYNC_STATUS, JSON.stringify({
        success: false,
        timestamp: Date.now(),
        error: error.message
      }));

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Merge conversations from localStorage and Firestore
   * @private
   */
  mergeConversations(localConversations, firestoreConversations) {
    const merged = new Map();

    // Add Firestore conversations first (they take precedence)
    for (const fsConv of firestoreConversations) {
      merged.set(fsConv.id, {
        ...fsConv,
        firestoreId: fsConv.id,
        source: 'firestore'
      });
    }

    // Add or merge local conversations
    for (const localConv of localConversations) {
      const firestoreId = localConv.firestoreId;

      if (firestoreId && merged.has(firestoreId)) {
        // Merge with Firestore version (keep newer data)
        const fsConv = merged.get(firestoreId);
        const localTime = localConv.updatedAt || localConv.createdAt || 0;
        const fsTime = fsConv.updatedAt || fsConv.createdAt || 0;

        if (localTime > fsTime) {
          // Local is newer, update Firestore version
          merged.set(firestoreId, {
            ...fsConv,
            ...localConv,
            firestoreId: firestoreId,
            source: 'merged'
          });
        }
      } else if (!firestoreId) {
        // Local-only conversation (not yet synced)
        merged.set(localConv.id, {
          ...localConv,
          source: 'local'
        });
      }
    }

    return Array.from(merged.values());
  }

  /**
   * Process offline queue
   * @private
   */
  async processOfflineQueue() {
    await this.offlineQueue.process();
  }

  /**
   * Start periodic sync
   * @private
   */
  startPeriodicSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }

    this.syncTimer = setInterval(() => {
      if (isOnline() && this.isInitialized) {
        this.syncConversations();
        this.processOfflineQueue();
      }
    }, SYNC_INTERVAL);
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  /**
   * Get sync status
   * @returns {Object} Current sync status
   */
  getSyncStatus() {
    try {
      const status = localStorage.getItem(STORAGE_KEYS.SYNC_STATUS);
      return status ? JSON.parse(status) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get last sync time
   * @returns {string|null} ISO string of last sync time
   */
  getLastSyncTime() {
    return localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
  }

  /**
   * Clear all local data (use with caution!)
   * @returns {boolean} Success status
   */
  clearAllLocalData() {
    try {
      log('warn', 'Clearing all local conversation data');
      localStorage.removeItem(STORAGE_KEYS.CONVERSATIONS);
      localStorage.removeItem(STORAGE_KEYS.OFFLINE_QUEUE);
      localStorage.removeItem(STORAGE_KEYS.SYNC_STATUS);
      localStorage.removeItem(STORAGE_KEYS.LAST_SYNC);
      localStorage.removeItem('conversation_service_logs');
      return true;
    } catch (error) {
      log('error', 'Failed to clear local data', { error: error.message });
      return false;
    }
  }

  /**
   * Export conversations for backup
   * @returns {Object} Exported data
   */
  async exportConversations() {
    this.ensureInitialized();

    const conversations = await this.loadConversations();
    const syncStatus = this.getSyncStatus();
    const lastSync = this.getLastSyncTime();

    return {
      version: '1.0',
      exportDate: new Date().toISOString(),
      userId: this.userId,
      conversations,
      syncStatus,
      lastSync,
      conversationCount: conversations.length
    };
  }

  /**
   * Import conversations from backup
   * @param {Object} data - Exported data
   * @returns {Promise<boolean>} Success status
   */
  async importConversations(data) {
    this.ensureInitialized();

    try {
      if (!data || !data.conversations || !Array.isArray(data.conversations)) {
        throw new Error('Invalid import data');
      }

      log('info', 'Importing conversations', { count: data.conversations.length });

      // Validate all conversations
      for (const conv of data.conversations) {
        validateConversation(conv);
      }

      // Save all conversations
      for (const conv of data.conversations) {
        await this.saveConversation(conv);
      }

      log('info', 'Import completed successfully');
      return true;
    } catch (error) {
      log('error', 'Import failed', { error: error.message });
      throw error;
    }
  }
}

// Create singleton instance
const conversationService = new ConversationService();

// Export service and utilities
export default conversationService;
export {
  LocalStorageManager,
  OfflineQueue,
  isOnline,
  checkStorageQuota,
  validateConversation,
  validateMessage,
  retryOperation
};